import { postDocument } from '../web-apis-client';
import { getSimpleRemoteConfig } from '../remote-config-manager';
import { enums } from '../.';
import { UploadFileInfo } from '@progress/kendo-react-upload';
import {
  IPostDocumentArgsWithCaseId,
  IPostDocumentArgsWithPlanId,
} from '../../data-models/web-apis';
import { log } from '../log';

export const convertExtensionToLowercase = (filename: string) => {
  const extension = filename.split('.').pop();
  const lower = extension?.toLowerCase();
  const noExt = filename.replace(/\.[^/.]+$/, '');
  return `${noExt}.${lower}`;
};

export const sendPercentValid = (p: number | null | undefined) => {
  return p && p > 0;
};

export const uploadFile = async (
  file: any,
  index: number,
  setFileAttributes: Function,
  setUploadTried: Function,
) => {
  if (file.sent) {
    return 'success';
  }
  setFileAttributes(file, index, true);
  try {
    if ((file.caseId || file.planId) && file.extension) {
      const pdArgs: IPostDocumentArgsWithCaseId | IPostDocumentArgsWithPlanId =
        {
          planId: 'test',
          fileName: file.name,
          contentType: documentContentTypeMap[file.extension.substring(1)],
          actionType: 'init',
        };
      if (file.caseId) {
        pdArgs.caseId = file.caseId;
      } else {
        pdArgs.planId = file.planId;
      }

      const response = await postDocument(pdArgs);

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', response.s3Url);
        xhr.setRequestHeader(
          'Content-Type',
          documentContentTypeMap[file.extension.substring(1)],
        );

        xhr.upload.onprogress = (e: ProgressEvent) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded * 100) / e.total);
            setFileAttributes(
              file,
              index,
              true,
              false,
              false,
              '',
              percentComplete,
            );
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            setFileAttributes(file, index, false, true, false, '', 100);
            resolve();
          } else {
            reject(new Error('Upload Failed'));
          }
        };

        xhr.onerror = () => reject(new Error('Upload Failed'));

        xhr.send(file.getRawFile?.());
      });

      return 'success';
    } else {
      throw new Error('Upload failed');
    }
  } catch (e) {
    log.error('Upload error:', e);
    setFileAttributes(
      file,
      index,
      false,
      false,
      true,
      'File failed to upload',
      null,
    );
    return 'failure';
  } finally {
    setUploadTried(true);
  }
};

const documentContentTypeMap: any = {
  bmp: 'image/bmp',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  gif: 'image/gif',
  jpg: 'image/jpg',
  jpeg: 'image/jpeg',
  pdf: 'application/pdf',
  rtf: 'application/rtf',
  png: 'image/png',
  txt: 'text/plain',
};

export const allowedFileTypes = Object.keys(documentContentTypeMap);

export const getFileSizeLimit = async (setFileSizeLimit: Function) => {
  const remoteConfig = await getSimpleRemoteConfig();
  setFileSizeLimit(remoteConfig[enums.RemoteConfigKeys.uploadLimit]);
};

export interface CustomUploadFileInfo extends UploadFileInfo {
  caseId?: string;
  planId?: string;
  sending?: boolean;
  sent?: boolean;
  sendPercent?: number | null;
  error?: boolean;
  errorMessage?: string;
}

export async function isPasswordProtected(file: File): Promise<boolean> {
  const arrayBuffer = await file.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  const textSample = new TextDecoder('latin1').decode(bytes);

  // Password check for PDF files
  if (file.type === 'application/pdf' || /\.pdf$/i.exec(file.name)) {
    const isEncrypted =
      textSample.includes('/Encrypt') &&
      (textSample.includes('/Filter') || textSample.includes('/Standard'));
    return isEncrypted;
  }

  // Password check for DOCX Files
  if (
    file.type ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    /\.docx$/i.exec(file.name)
  ) {
    try {
      const dataView = new DataView(bytes.buffer);
      const LOCAL_FILE_HEADER = 0x04034b50;

      let foundHeader = false;
      for (let i = 0; i < bytes.length - 4; i++) {
        const sig =
          dataView.getUint8(i) |
          (dataView.getUint8(i + 1) << 8) |
          (dataView.getUint8(i + 2) << 16) |
          (dataView.getUint8(i + 3) << 24);

        if (sig === LOCAL_FILE_HEADER) {
          foundHeader = true;
          break;
        }
      }

      const isEncrypted = !foundHeader;
      return isEncrypted;
    } catch (err) {
      log.error('DOCX encryption check failed:', err);
      return true;
    }
  }

  // Password check for DOC Files
  const checkDocEncryption = (docBytes: Uint8Array): boolean => {
    const encryptionMarker = 0x13;
    return Array.prototype.includes.call(docBytes, encryptionMarker);
  };
  if (file.type === 'application/msword' || /\.doc$/i.exec(file.name)) {
    return checkDocEncryption(bytes);
  }
  // Password check for RTF Files
  if (file.type === 'application/rtf' || /\.rtf$/i.exec(file.name)) {
    const rtfText = textSample.toLowerCase();
    const hasProtection =
      rtfText.includes(String.raw`\password`) ||
      rtfText.includes(String.raw`\*passwordhash`);
    return hasProtection;
  }
  // Skip Password check for JPG,JPEG, PNG, GIF, BMP
  if (
    file.type.startsWith('image/') ||
    /\.(jpg|jpeg|png|gif|bmp)$/i.exec(file.name)
  ) {
    return false;
  }
  // Skip Password check for TXT Files
  if (file.type === 'text/plain' || /\.txt$/i.exec(file.name)) {
    return false;
  }
  return false;
}

const formatFileList = (files: string[]): string => {
  return files.map((f) => `"${f}"`).join(', ');
};

export function getPasswordErrorMessage(files: string[]): string {
  if (files.length === 0) return '';

  const fileList = formatFileList(files);
  const plural = files.length > 1;

  return `The ${plural ? 'files' : 'file'} ${fileList} ${
    plural ? 'are' : 'is'
  } password protected. Please remove the ${
    plural ? 'files' : 'file'
  } and try a different one.`;
}
